import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Stack, Button, CircularProgress, Tabs, Tab, Paper,
  Select, MenuItem, FormControl, InputLabel, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, IconButton, Autocomplete,
  Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridColumnGroupingModel } from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon, GroupAdd as MasivaIcon,
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  AccessTime as TimeIcon, Calculate as CalcIcon,
} from '@mui/icons-material';
import { AsistenciaService } from '../services/asistenciaService';
import { PeriodoService } from '../../periodos/services/periodoService';
import { TrabajadorService } from '../../trabajadores/services/trabajadorService';
import type { PeriodoPlanilla } from '../../periodos/types/periodo';
import type { Trabajador } from '../../trabajadores/types/trabajador';
import type { Asistencia, ResumenAsistenciaPeriodo, Incidencia } from '../types/asistencia';
import { TIPOS_INCIDENCIA, TIPOS_ASISTENCIA } from '../types/asistencia';

export default function AsistenciaPage() {
  const [periodos, setPeriodos] = useState<PeriodoPlanilla[]>([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState<number>(0);
  const [tab, setTab] = useState(0);
  const [resumen, setResumen] = useState<ResumenAsistenciaPeriodo | null>(null);
  const [incidencias, setIncidencias] = useState<Incidencia[]>([]);
  const [loading, setLoading] = useState(false);
  const [incDialog, setIncDialog] = useState(false);
  const [incForm, setIncForm] = useState<Partial<Incidencia>>({});
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [selectedTrabajador, setSelectedTrabajador] = useState<Trabajador | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Tab 2: Registro Diario
  const [registroDiario, setRegistroDiario] = useState<Asistencia[]>([]);
  const [filtroTrabajador, setFiltroTrabajador] = useState<Trabajador | null>(null);
  const [loadingDiario, setLoadingDiario] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Asistencia>>({});

  useEffect(() => {
    loadPeriodos();
    loadTrabajadores();
  }, []);

  const loadTrabajadores = async () => {
    try {
      const data = await TrabajadorService.getAll();
      setTrabajadores(data.filter(t => t.activo));
    } catch { /* handled */ }
  };

  const loadPeriodos = async () => {
    try {
      const data = await PeriodoService.getAll();
      const sorted = data.sort((a, b) => b.anio - a.anio || b.mes - a.mes);
      setPeriodos(sorted);
      if (sorted.length > 0) {
        setSelectedPeriodo(sorted[0].idPeriodo);
      }
    } catch { /* handled by interceptor */ }
  };

  useEffect(() => {
    if (selectedPeriodo) loadData();
  }, [selectedPeriodo]);

  const loadData = async () => {
    if (!selectedPeriodo) return;
    setLoading(true);
    try {
      const [res, inc] = await Promise.all([
        AsistenciaService.getResumen(selectedPeriodo),
        AsistenciaService.getIncidencias(selectedPeriodo),
      ]);
      setResumen(res);
      setIncidencias(inc);
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  const loadRegistroDiario = async (idTrabajador?: number) => {
    if (!selectedPeriodo) return;
    setLoadingDiario(true);
    try {
      const data = await AsistenciaService.getByPeriodo(selectedPeriodo, idTrabajador);
      setRegistroDiario(data);
    } catch { /* handled */ }
    finally { setLoadingDiario(false); }
  };

  useEffect(() => {
    if (tab === 2 && selectedPeriodo) {
      loadRegistroDiario(filtroTrabajador?.idTrabajador);
    }
  }, [tab, selectedPeriodo, filtroTrabajador]);

  const handleRegistroMasivo = async () => {
    if (!selectedPeriodo) return;
    setLoading(true);
    setMessage(null);
    try {
      const trabIds = resumen?.detalle.map(d => d.idTrabajador) ?? [];
      if (trabIds.length === 0) {
        setMessage('No hay trabajadores activos para registrar');
        return;
      }
      const result = await AsistenciaService.registrarMasiva(selectedPeriodo, trabIds);
      setMessage(`Se crearon ${result.registrosCreados} registros de asistencia`);
      await loadData();
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  const handleRecalcularHoras = async () => {
    if (!selectedPeriodo) return;
    setLoading(true);
    setMessage(null);
    try {
      const result = await AsistenciaService.recalcularHoras(selectedPeriodo);
      setMessage(`Se recalcularon las horas de ${result.registrosActualizados} registros`);
      await loadData();
      if (tab === 2) await loadRegistroDiario(filtroTrabajador?.idTrabajador);
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  const handleSaveIncidencia = async () => {
    if (!selectedTrabajador) return;
    try {
      if (editingId) {
        await AsistenciaService.updateIncidencia(editingId, {
          ...incForm,
          idTrabajador: selectedTrabajador.idTrabajador,
          idPeriodo: selectedPeriodo,
        });
      } else {
        await AsistenciaService.registrarIncidencia({
          ...incForm,
          idTrabajador: selectedTrabajador.idTrabajador,
          idPeriodo: selectedPeriodo,
        });
      }
      setIncDialog(false);
      setIncForm({});
      setSelectedTrabajador(null);
      setEditingId(null);
      await loadData();
    } catch { /* handled */ }
  };

  const handleEditIncidencia = (inc: Incidencia) => {
    setIncForm({
      tipoIncidencia: inc.tipoIncidencia,
      fechaInicio: inc.fechaInicio?.substring(0, 10),
      fechaFin: inc.fechaFin?.substring(0, 10),
      observaciones: inc.observaciones,
      afectaRemuneracion: inc.afectaRemuneracion,
    });
    const trab = trabajadores.find(t => t.idTrabajador === inc.idTrabajador);
    setSelectedTrabajador(trab ?? null);
    setEditingId(inc.idIncidencia);
    setIncDialog(true);
  };

  const handleDeleteIncidencia = async (id: number) => {
    try {
      await AsistenciaService.deleteIncidencia(id);
      await loadData();
    } catch { /* handled */ }
  };

  // ── Edit Asistencia diaria ──
  const handleEditAsistencia = (row: Asistencia) => {
    setEditForm({
      idAsistencia: row.idAsistencia,
      idTrabajador: row.idTrabajador,
      idPeriodo: row.idPeriodo,
      fecha: row.fecha,
      horaEntrada: row.horaEntrada ?? '',
      horaSalida: row.horaSalida ?? '',
      tipoAsistencia: row.tipoAsistencia,
      minutosTardanza: row.minutosTardanza,
      minutosPermiso: row.minutosPermiso,
      justificado: row.justificado,
      observaciones: row.observaciones ?? '',
    });
    setEditDialog(true);
  };

  const handleNewAsistencia = (idTrabajador: number, dia: number) => {
    const fecha = `${anioActual}-${mesActual.toString().padStart(2, '0')}-${dia.toString().padStart(2, '0')}`;
    setEditForm({
      idAsistencia: 0,
      idTrabajador,
      idPeriodo: selectedPeriodo,
      fecha,
      horaEntrada: '',
      horaSalida: '',
      tipoAsistencia: 'ASISTIO',
      minutosTardanza: 0,
      minutosPermiso: 0,
      justificado: false,
      observaciones: '',
    });
    setEditDialog(true);
  };

  const handleSaveAsistencia = async () => {
    try {
      const payload = {
        ...editForm,
        horaEntrada: editForm.horaEntrada || null,
        horaSalida: editForm.horaSalida || null,
      } as Asistencia;
      if (editForm.idAsistencia) {
        await AsistenciaService.update(editForm.idAsistencia, payload);
      } else {
        await AsistenciaService.registrar(payload);
      }
      setEditDialog(false);
      setEditForm({});
      await loadRegistroDiario(filtroTrabajador?.idTrabajador);
      await loadData();
    } catch { /* handled */ }
  };

  // Drill down from resumen to registro diario
  const handleDrillDown = (idTrabajador: number) => {
    const trab = trabajadores.find(t => t.idTrabajador === idTrabajador);
    setFiltroTrabajador(trab ?? null);
    setTab(2);
  };

  // ── Format time for display ──
  const formatTime = (val: string | null | undefined) => {
    if (!val) return '—';
    return val.substring(0, 5);
  };

  // ── Calcular horas cliente (fallback si backend no las tiene) ──
  const calcHoras = (a: Asistencia) => {
    if (a.horasTrabajadas > 0) return { ht: a.horasTrabajadas, e25: a.horasExtras25, e35: a.horasExtras35 };
    if (!a.horaEntrada || !a.horaSalida) return null;
    const toMin = (t: string) => { const p = t.split(':'); return +p[0] * 60 + +p[1]; };
    let entrada = toMin(a.horaEntrada);
    let salida = toMin(a.horaSalida);
    if (salida <= entrada) salida += 24 * 60;
    const total = Math.round(((salida - entrada) / 60) * 100) / 100;
    if (total <= 0) return null;
    // No calcular extras en frontend — el backend usa el turno del trabajador
    return { ht: total, e25: 0, e35: 0 };
  };

  // ── Columns ──
  const resumenColumns: GridColDef[] = [
    { field: 'nombreTrabajador', headerName: 'Trabajador', flex: 1, minWidth: 200 },
    { field: 'diasLaborados', headerName: 'Días Lab.', width: 90, type: 'number',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{p.value}</Typography>
      )},
    { field: 'diasNoLaborados', headerName: 'Días No Lab.', width: 100, type: 'number',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: p.value > 0 ? 'error.main' : 'text.secondary' }}>{p.value}</Typography>
      )},
    { field: 'faltas', headerName: 'Faltas', width: 70, type: 'number',
      renderCell: (p) => p.value > 0 ? <Chip label={p.value} size="small" color="error" /> : '0' },
    { field: 'faltasJustificadas', headerName: 'F. Just.', width: 80, type: 'number' },
    { field: 'tardanzas', headerName: 'Tard.', width: 70, type: 'number',
      renderCell: (p) => p.value > 0 ? <Chip label={p.value} size="small" color="warning" /> : '0' },
    { field: 'minutosTardanzaTotal', headerName: 'Min. Tard.', width: 90, type: 'number' },
    { field: 'permisos', headerName: 'Permisos', width: 80, type: 'number' },
    { field: 'descansosMedicos', headerName: 'Desc. Méd.', width: 90, type: 'number',
      renderCell: (p) => p.value > 0 ? <Chip label={p.value} size="small" color="info" /> : '0' },
    { field: 'totalHorasTrabajadas', headerName: 'Hrs Trab.', width: 90, type: 'number',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{Number(p.value || 0).toFixed(1)}</Typography> },
    { field: 'totalHorasExtras25', headerName: 'HE 25%', width: 80, type: 'number',
      renderCell: (p) => p.value > 0 ? <Chip label={Number(p.value).toFixed(1)} size="small" color="warning" /> : '0' },
    { field: 'totalHorasExtras35', headerName: 'HE 35%', width: 80, type: 'number',
      renderCell: (p) => p.value > 0 ? <Chip label={Number(p.value).toFixed(1)} size="small" color="error" /> : '0' },
    { field: 'acciones', headerName: '', width: 60, sortable: false,
      renderCell: (p) => (
        <Tooltip title="Ver registro diario">
          <IconButton size="small" color="primary" onClick={() => handleDrillDown(p.row.idTrabajador)}>
            <TimeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )},
  ];

  const resumenColumnGrouping: GridColumnGroupingModel = [
    {
      groupId: 'resumen',
      headerName: 'Resumen',
      children: [{ field: 'diasLaborados' }, { field: 'diasNoLaborados' }],
    },
    {
      groupId: 'detalle',
      headerName: 'Detalle (compone Días No Lab.)',
      children: [
        { field: 'faltas' }, { field: 'faltasJustificadas' },
        { field: 'tardanzas' }, { field: 'minutosTardanzaTotal' },
        { field: 'permisos' }, { field: 'descansosMedicos' },
      ],
    },
    {
      groupId: 'horas',
      headerName: 'Horas',
      children: [
        { field: 'totalHorasTrabajadas' },
        { field: 'totalHorasExtras25' },
        { field: 'totalHorasExtras35' },
      ],
    },
  ];

  const incidenciaColumns: GridColDef[] = [
    { field: 'trabajadorNombre', headerName: 'Trabajador', flex: 1, minWidth: 200,
      valueGetter: (_v, row) => {
        const t = trabajadores.find(tr => tr.idTrabajador === row.idTrabajador);
        return t?.personal?.personaContacto ?? `#${row.idTrabajador}`;
      }},
    { field: 'tipoIncidencia', headerName: 'Tipo', width: 160,
      renderCell: (p) => {
        const t = TIPOS_INCIDENCIA.find(ti => ti.value === p.value);
        return <Chip label={t?.label ?? p.value} size="small" />;
      }},
    { field: 'fechaInicio', headerName: 'Desde', width: 110, valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'fechaFin', headerName: 'Hasta', width: 110, valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'diasAfectados', headerName: 'Días', width: 70, type: 'number' },
    { field: 'afectaRemuneracion', headerName: 'Afecta Rem.', width: 100, type: 'boolean' },
    { field: 'observaciones', headerName: 'Observaciones', flex: 1, minWidth: 150 },
    { field: 'acciones', headerName: '', width: 90, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0}>
          <IconButton size="small" color="primary" onClick={() => handleEditIncidencia(p.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteIncidencia(p.row.idIncidencia)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )},
  ];

  // ── Matrix view helpers ──
  const DOW = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
  const STATUS_SHORT: Record<string, string> = {
    ASISTIO: 'A', FALTA: 'F', TARDANZA: 'T', PERMISO: 'P',
    DESCANSO_MEDICO: 'DM', VACACIONES: 'V', FERIADO: 'Fe',
  };

  const periodoActual = periodos.find(p => p.idPeriodo === selectedPeriodo);
  const mesActual = periodoActual?.mes ?? 1;
  const anioActual = periodoActual?.anio ?? 2026;
  const diasEnMes = new Date(anioActual, mesActual, 0).getDate();

  const pivotData = useMemo(() => {
    const byWorker = new Map<number, { nombre: string; dias: Map<number, Asistencia> }>();
    registroDiario.forEach(r => {
      if (!byWorker.has(r.idTrabajador)) {
        const t = trabajadores.find(tr => tr.idTrabajador === r.idTrabajador);
        byWorker.set(r.idTrabajador, {
          nombre: t?.personal?.personaContacto ?? `#${r.idTrabajador}`,
          dias: new Map(),
        });
      }
      const dia = new Date(r.fecha).getDate();
      byWorker.get(r.idTrabajador)!.dias.set(dia, r);
    });
    return Array.from(byWorker.entries()).map(([id, data]) => ({
      idTrabajador: id,
      nombreTrabajador: data.nombre,
      dias: data.dias,
    }));
  }, [registroDiario, trabajadores]);

  const diasArray = useMemo(() =>
    Array.from({ length: diasEnMes }, (_, i) => {
      const d = i + 1;
      const fecha = new Date(anioActual, mesActual - 1, d);
      const dow = fecha.getDay();
      return { dia: d, dow, label: DOW[dow], isWeekend: dow === 0 || dow === 6 };
    }), [diasEnMes, mesActual, anioActual]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Asistencia y Control de Tiempo</Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Periodo</InputLabel>
          <Select value={selectedPeriodo || ''} label="Periodo" onChange={(e) => setSelectedPeriodo(Number(e.target.value))}>
            {periodos.map(p => (
              <MenuItem key={p.idPeriodo} value={p.idPeriodo}>{`${p.mes.toString().padStart(2, '0')}/${p.anio}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>Actualizar</Button>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <MasivaIcon />}
          onClick={handleRegistroMasivo} disabled={loading}>
          Registro Masivo
        </Button>
        <Button variant="outlined" color="secondary" startIcon={<CalcIcon />}
          onClick={handleRecalcularHoras} disabled={loading}>
          Recalcular Horas
        </Button>
      </Stack>

      {resumen && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="body2" color="text.secondary">Trabajadores</Typography>
              <Typography variant="h6">{resumen.totalTrabajadores}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Días Hábiles</Typography>
              <Typography variant="h6">{resumen.diasHabiles}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Periodo</Typography>
              <Typography variant="h6">{resumen.mes.toString().padStart(2, '0')}/{resumen.anio}</Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Resumen de Asistencia" />
        <Tab label="Incidencias" />
        <Tab label="Registro Diario" icon={<TimeIcon />} iconPosition="start" />
      </Tabs>

      {/* Tab 0: Resumen */}
      {tab === 0 && resumen && (
        <DataGrid
          rows={resumen.detalle}
          columns={resumenColumns}
          columnGroupingModel={resumenColumnGrouping}
          getRowId={(row) => row.idTrabajador}
          autoHeight
          density="compact"
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      )}

      {/* Tab 1: Incidencias */}
      {tab === 1 && (
        <>
          <Button variant="contained" startIcon={<AddIcon />} sx={{ mb: 2 }}
            onClick={() => { setIncForm({ afectaRemuneracion: true }); setSelectedTrabajador(null); setEditingId(null); setIncDialog(true); }}>
            Registrar Incidencia
          </Button>
          <DataGrid
            rows={incidencias}
            columns={incidenciaColumns}
            getRowId={(row) => row.idIncidencia}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25]}
          />
        </>
      )}

      {/* Tab 2: Registro Diario (Matrix View) */}
      {tab === 2 && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 300 }}
              options={trabajadores}
              getOptionLabel={(t) => t.personal?.personaContacto ?? `#${t.idTrabajador}`}
              value={filtroTrabajador}
              onChange={(_, val) => setFiltroTrabajador(val)}
              renderInput={(params) => <TextField {...params} label="Filtrar por trabajador" />}
              isOptionEqualToValue={(opt, val) => opt.idTrabajador === val.idTrabajador}
            />
            {filtroTrabajador && (
              <Button size="small" variant="text" onClick={() => setFiltroTrabajador(null)}>
                Ver todos
              </Button>
            )}
            {loadingDiario && <CircularProgress size={20} />}
          </Stack>

          {/* Legend */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {TIPOS_ASISTENCIA.map(t => (
              <Chip key={t.value} label={`${STATUS_SHORT[t.value] ?? '?'} ${t.label}`} size="small"
                sx={{ bgcolor: t.color, color: '#fff', fontWeight: 600, fontSize: 11 }} />
            ))}
          </Stack>

          {registroDiario.length === 0 && !loadingDiario && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No hay registros de asistencia. Use "Registro Masivo" para crear los registros del periodo, luego edite las horas aquí.
            </Alert>
          )}

          {pivotData.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 700, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky', left: 0, zIndex: 3,
                        bgcolor: 'background.paper', minWidth: 180, maxWidth: 180, fontWeight: 700,
                        borderRight: '2px solid', borderRightColor: 'divider',
                      }}
                    >
                      Trabajador
                    </TableCell>
                    <TableCell
                      sx={{
                        position: 'sticky', left: 180, zIndex: 3,
                        bgcolor: 'background.paper', width: 38, minWidth: 38, maxWidth: 38,
                        borderRight: '1px solid', borderRightColor: 'divider',
                        fontSize: 9, fontWeight: 700,
                      }}
                    />
                    {diasArray.map(({ dia, label, isWeekend }) => (
                      <TableCell
                        key={dia} align="center"
                        sx={{
                          px: 0.25, minWidth: 48, maxWidth: 48,
                          bgcolor: isWeekend ? 'grey.100' : 'background.paper',
                        }}
                      >
                        <Typography variant="caption" sx={{
                          fontSize: 10, display: 'block', lineHeight: 1,
                          color: isWeekend ? 'error.main' : 'text.secondary',
                        }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{
                          fontWeight: 700, display: 'block', lineHeight: 1.4,
                          color: isWeekend ? 'error.main' : 'text.primary',
                        }}>
                          {dia}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ minWidth: 55, bgcolor: '#e8f5e9', fontWeight: 700, fontSize: 11, borderLeft: '2px solid', borderLeftColor: 'divider' }}>
                      Hrs Trab
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 50, bgcolor: '#fff3e0', fontWeight: 700, fontSize: 11 }}>
                      HE 25%
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 50, bgcolor: '#fce4ec', fontWeight: 700, fontSize: 11 }}>
                      HE 35%
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pivotData.map(row => {
                    const dias = Array.from(row.dias.values());
                    const totalHrs = dias.reduce((s, a) => { const h = calcHoras(a); return s + (h?.ht || 0); }, 0);
                    const totalE25 = dias.reduce((s, a) => { const h = calcHoras(a); return s + (h?.e25 || 0); }, 0);
                    const totalE35 = dias.reduce((s, a) => { const h = calcHoras(a); return s + (h?.e35 || 0); }, 0);

                    const subRows = [
                      { key: 'HI', label: 'HI', getValue: (a: Asistencia) => a.horaEntrada ? formatTime(a.horaEntrada) : '', color: 'text.secondary' },
                      { key: 'HF', label: 'HF', getValue: (a: Asistencia) => a.horaSalida ? formatTime(a.horaSalida) : '', color: 'text.secondary' },
                      { key: 'HN', label: 'HN', getValue: (a: Asistencia) => { const h = calcHoras(a); return h ? h.ht.toFixed(1) : ''; }, color: 'text.primary', bold: true },
                      { key: 'HE25', label: 'HE25', getValue: (a: Asistencia) => { const h = calcHoras(a); return h && h.e25 > 0 ? h.e25.toFixed(1) : ''; }, color: '#ed6c02' },
                      { key: 'HE35', label: 'HE35', getValue: (a: Asistencia) => { const h = calcHoras(a); return h && h.e35 > 0 ? h.e35.toFixed(1) : ''; }, color: '#d32f2f' },
                    ];

                    return subRows.map((sub, subIdx) => (
                      <TableRow key={`${row.idTrabajador}-${sub.key}`}>
                        {subIdx === 0 && (
                          <TableCell
                            rowSpan={5}
                            sx={{
                              position: 'sticky', left: 0, zIndex: 1,
                              bgcolor: 'background.paper', fontWeight: 600,
                              borderRight: '2px solid', borderRightColor: 'divider',
                              whiteSpace: 'nowrap', fontSize: 12, verticalAlign: 'middle',
                              borderBottom: '2px solid', borderBottomColor: 'divider',
                              minWidth: 180, maxWidth: 180,
                            }}
                          >
                            {row.nombreTrabajador}
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            position: 'sticky', left: 180, zIndex: 1,
                            bgcolor: 'background.paper',
                            fontWeight: 700, fontSize: 9, py: 0, px: 0.5,
                            borderRight: '1px solid', borderRightColor: 'divider',
                            color: sub.color, width: 38, minWidth: 38, maxWidth: 38,
                            ...(subIdx === 4 && { borderBottom: '2px solid', borderBottomColor: 'divider' }),
                          }}
                        >
                          {sub.label}
                        </TableCell>
                        {diasArray.map(({ dia, isWeekend }) => {
                          const asistencia = row.dias.get(dia);
                          const val = asistencia ? sub.getValue(asistencia) : '';
                          return (
                            <TableCell
                              key={dia} align="center"
                              onClick={() => asistencia ? handleEditAsistencia(asistencia) : (subIdx === 0 && handleNewAsistencia(row.idTrabajador, dia))}
                              sx={{
                                py: 0, px: 0.25, fontSize: 10,
                                bgcolor: isWeekend ? 'grey.50' : 'transparent',
                                fontWeight: sub.bold ? 700 : 400,
                                color: sub.color,
                                cursor: (asistencia || subIdx === 0) ? 'pointer' : 'default',
                                '&:hover': (asistencia || subIdx === 0) ? { bgcolor: 'action.hover' } : {},
                                ...(subIdx === 4 && { borderBottom: '2px solid', borderBottomColor: 'divider' }),
                              }}
                            >
                              {val}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{
                          fontWeight: 700, fontSize: 11, py: 0,
                          bgcolor: '#e8f5e9',
                          borderLeft: '2px solid', borderLeftColor: 'divider',
                          ...(subIdx === 4 && { borderBottom: '2px solid', borderBottomColor: 'divider' }),
                        }}>
                          {sub.key === 'HN' ? (totalHrs > 0 ? totalHrs.toFixed(1) : '—') : ''}
                        </TableCell>
                        <TableCell align="center" sx={{
                          fontWeight: 700, fontSize: 11, py: 0,
                          bgcolor: '#fff3e0', color: '#ed6c02',
                          ...(subIdx === 4 && { borderBottom: '2px solid', borderBottomColor: 'divider' }),
                        }}>
                          {sub.key === 'HE25' ? (totalE25 > 0 ? totalE25.toFixed(1) : '—') : ''}
                        </TableCell>
                        <TableCell align="center" sx={{
                          fontWeight: 700, fontSize: 11, py: 0,
                          bgcolor: '#fce4ec', color: '#d32f2f',
                          ...(subIdx === 4 && { borderBottom: '2px solid', borderBottomColor: 'divider' }),
                        }}>
                          {sub.key === 'HE35' ? (totalE35 > 0 ? totalE35.toFixed(1) : '—') : ''}
                        </TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Dialog: Incidencia */}
      <Dialog open={incDialog} onClose={() => setIncDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar Incidencia' : 'Registrar Incidencia'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              options={trabajadores}
              getOptionLabel={(t) => t.personal?.personaContacto ?? ''}
              value={selectedTrabajador}
              onChange={(_, val) => setSelectedTrabajador(val)}
              renderInput={(params) => <TextField {...params} label="Buscar trabajador" />}
              isOptionEqualToValue={(opt, val) => opt.idTrabajador === val.idTrabajador}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo Incidencia</InputLabel>
              <Select value={incForm.tipoIncidencia ?? ''} label="Tipo Incidencia"
                onChange={(e) => setIncForm(p => ({ ...p, tipoIncidencia: e.target.value }))}>
                {TIPOS_INCIDENCIA.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Fecha Inicio" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={incForm.fechaInicio ?? ''} onChange={(e) => setIncForm(p => ({ ...p, fechaInicio: e.target.value }))} />
            <TextField label="Fecha Fin" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={incForm.fechaFin ?? ''} onChange={(e) => setIncForm(p => ({ ...p, fechaFin: e.target.value }))} />
            <TextField label="Observaciones" size="small" fullWidth multiline rows={2}
              value={incForm.observaciones ?? ''} onChange={(e) => setIncForm(p => ({ ...p, observaciones: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveIncidencia}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar Asistencia Diaria */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editForm.idAsistencia ? 'Editar Asistencia' : 'Nueva Asistencia'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Fecha"
              size="small"
              fullWidth
              value={editForm.fecha ? new Date(editForm.fecha).toLocaleDateString('es-PE') : ''}
              slotProps={{ input: { readOnly: true } }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select value={editForm.tipoAsistencia ?? ''} label="Estado"
                onChange={(e) => setEditForm(p => ({ ...p, tipoAsistencia: e.target.value }))}>
                {TIPOS_ASISTENCIA.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Hora Entrada"
              type="time"
              size="small"
              fullWidth
              value={editForm.horaEntrada ? editForm.horaEntrada.substring(0, 5) : ''}
              onChange={(e) => setEditForm(p => ({ ...p, horaEntrada: e.target.value ? e.target.value + ':00' : '' }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Hora Salida"
              type="time"
              size="small"
              fullWidth
              value={editForm.horaSalida ? editForm.horaSalida.substring(0, 5) : ''}
              onChange={(e) => setEditForm(p => ({ ...p, horaSalida: e.target.value ? e.target.value + ':00' : '' }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Observaciones"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={editForm.observaciones ?? ''}
              onChange={(e) => setEditForm(p => ({ ...p, observaciones: e.target.value }))}
            />

            {editForm.minutosTardanza && editForm.minutosTardanza > 0 ? (
              <Alert severity="warning">Tardanza: {editForm.minutosTardanza} minutos</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAsistencia}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
